import {
  GridColumnVisibilityModel,
  GridHeaderSelectionCheckboxParams,
  GridRowModel,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import TransactionsDataGrid, {
  TransactionsTableLargeDesigns,
} from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import TransactionsTableBulkFloatingToolbar from 'src/components/TransactionsTableBulkFloatingToolbar/TransactionsTableBulkFloatingToolbar'
import TransactionsTableNeedsApprovalBulkFloatingToolbar from 'src/components/TransactionsTableNeedsApprovalBulkFloatingToolbar/TransactionsTableNeedsApprovalBulkFloatingToolbar'
import { useBackgroundDispatch } from 'src/hooks'
import {
  TransactionListModel,
  TransactionSort,
} from 'src/models/transaction.models'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { createSetIsSyncTransactionsPopupOpen } from 'src/redux/actions/app.actions'
import { updateSelectedTransactions } from 'src/redux/actions/data/transactions.actions'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectSelectedTransactionIdsFromState } from 'src/redux/selectors/data/transactions.selectors'
import { InfiniteScrollProps } from 'src/types'

export interface TransactionListCheckboxProps {
  canSelectTransaction: (transaction: TransactionListModel) => boolean
  handleCheckboxClick?: (selection: GridRowSelectionModel) => void
  handleHeaderCheckboxClick?: (
    params: GridHeaderSelectionCheckboxParams,
  ) => void
}

interface FullTransactionsTableProps {
  className?: string
  transactions: TransactionListModel[]
  fullyLoadedTransactions: boolean
  emptyStateDesign: TableEmptyStateDesigns
  design: TransactionsTableLargeDesigns
  defaultColumnVisibilityModel: GridColumnVisibilityModel
  infiniteScrollProps?: InfiniteScrollProps<TransactionsFiltersBody>
  filters?: TransactionsFiltersBody
  vcRefFields?: boolean
  reportId?: string
  clientHeight: number
  setSort?: (sort: TransactionSort) => void
  preventBulkActions?: boolean
  rowHoverActions?: (params: GridRowModel) => React.ReactElement
  rowApprovalActions?: (params: GridRowModel) => React.ReactElement
  context: TransactionReportType
}

const FullTransactionsTable: React.FC<FullTransactionsTableProps> = ({
  context = TransactionReportType.Other,
  ...props
}) => {
  const apiRef = useGridApiRef()
  const { data: user } = useQueryOwnUser({})
  const { data: expenseDetails } = useQueryExpenseCategories({ filters: {} })
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  const isEmpty =
    props.transactions.length === 0 && props.fullyLoadedTransactions
  const selectedTransactionIds = useSelector(
    selectSelectedTransactionIdsFromState,
  )
  const selectedTransactions = props.transactions.filter((transaction) =>
    selectedTransactionIds.has(transaction.id),
  )

  const { show_automated_expense_review: showAutomatedExpenseReview } =
    useSelector(selectFeatureFlaggerFromState)

  const dispatch = useBackgroundDispatch()

  // clear selected transactions on unmount
  useEffect(() => {
    return () => {
      dispatch(updateSelectedTransactions())
    }
  }, [])

  useEffect(() => {
    apiRef.current?.setRowSelectionModel({
      type: 'include',
      ids: selectedTransactionIds,
    })
  }, [selectedTransactionIds])

  const canSelectTransaction = (transaction: TransactionListModel): boolean => {
    return (
      [
        TransactionReportType.ExpenseReport,
        TransactionReportType.AccountingTransactions,
        TransactionReportType.NeedsApproval,
      ].includes(context) ||
      transaction.notPushedToConnectedPlatform(
        activeIntegration?.connectedPlatform,
      )
    )
  }

  const handleCheckboxClick = (selection: GridRowSelectionModel): void => {
    dispatch(updateSelectedTransactions(selection.ids))
  }

  const handleCloseToolbar = useCallback((): void => {
    dispatch(updateSelectedTransactions())
  }, [dispatch, apiRef])

  const checkboxProps: TransactionListCheckboxProps | undefined =
    useMemo(() => {
      const hasExpenseDetails =
        expenseDetails !== undefined &&
        expenseDetails.length > 0 &&
        context !== TransactionReportType.NeedsApproval

      const isReportType = [
        TransactionReportType.ExpenseReport,
        TransactionReportType.AccountingTransactions,
        TransactionReportType.NeedsApproval,
      ].includes(context)

      const hasIntegrationWithPermission =
        activeIntegration !== undefined && user?.hasOrgBookkeeperPermissions

      const canShowCheckbox =
        (!showAutomatedExpenseReview || !props.reportId) &&
        !props.preventBulkActions &&
        (hasExpenseDetails || isReportType || hasIntegrationWithPermission)

      return canShowCheckbox
        ? {
            canSelectTransaction,
            handleCheckboxClick,
          }
        : undefined
    }, [
      expenseDetails,
      context,
      activeIntegration,
      user?.hasOrgBookkeeperPermissions,
      showAutomatedExpenseReview,
      props.reportId,
      props.preventBulkActions,
      canSelectTransaction,
      handleCheckboxClick,
    ])

  return (
    <div>
      {isEmpty ? (
        <TableEmptyState design={props.emptyStateDesign} />
      ) : (
        props.infiniteScrollProps &&
        props.filters && (
          <TransactionsDataGrid
            apiRef={apiRef}
            transactions={props.transactions}
            infiniteScrollProps={props.infiniteScrollProps}
            design={props.design}
            filters={props.filters}
            checkboxProps={checkboxProps}
            defaultColumnVisibilityModel={props.defaultColumnVisibilityModel}
            clientHeight={props.clientHeight}
            reportId={props.reportId}
            setSort={props.setSort}
            rowHoverActions={props.rowHoverActions}
            rowApprovalActions={props.rowApprovalActions}
            context={context}
          />
        )
      )}
      {context === TransactionReportType.NeedsApproval ? (
        <TransactionsTableNeedsApprovalBulkFloatingToolbar
          selectedTransactions={selectedTransactions}
          handleCloseToolbar={handleCloseToolbar}
          filters={props.filters}
        />
      ) : (
        <TransactionsTableBulkFloatingToolbar
          context={context}
          filters={props.filters}
          selectedTransactions={selectedTransactions}
          showSyncWithIntegration={!!user?.hasOrgBookkeeperPermissions}
          handleCloseToolbar={handleCloseToolbar}
          handleSync={(): void =>
            dispatch(createSetIsSyncTransactionsPopupOpen(true))
          }
          orgExpenseReportId={props.reportId}
        />
      )}
    </div>
  )
}

export default FullTransactionsTable
